/**
 * A névtábla élő előnézete a pult-modálisokban (#168, Karlo terve 2026-09-16).
 *
 * MIÉRT IFRAME, ÉS MIÉRT A `buildBadgeHtml`-BŐL. Karlo tervében mindhárom
 * modális mutat egy „Matrica előnézet" dobozt, és a kliens ez alapján fogja
 * jóváhagyni a címkét. Ha az előnézetet külön JSX rajzolná meg, két forrás
 * lenne ugyanarra a címkére: az egyik a képernyőn, a másik a papíron. Egy
 * betűméret-hangolás a próbanyomtatás után az egyiken átmenne, a másikon nem
 * — és a pultnál derülne ki, hogy amit jóváhagytak, nem az, ami kijön.
 *
 * Ezért az előnézet UGYANAZT a dokumentumot tölti be, amit a nyomtatás küld a
 * nyomtatónak. A nagyítás egy `transform: scale()`, tehát a tartalom betű
 * szerint azonos — csak nagyobb.
 *
 * A `scale` a doboz szélességéhez igazodik, nem rögzített szorzó: a modális
 * szűkebb is lehet egy kisebb laptopképernyőn.
 *
 * VIGYÁZAT A „4,3x"-RE. Karlo jegyzete és e fájl korábbi kommentje is ~4,3x
 * nagyításról beszélt a 70 mm-es címkén. Ez félreolvasás: a 300 / 70 = 4,3 a
 * PIXEL/MILLIMÉTER arány, nem a nagyítás. A tényleges szorzó a 96 dpi-s
 * természetes mérethez képest értendő — 70 mm-en 300/264,6 = 1,13x, 60 mm-en
 * 300/226,8 = 1,32x. Az előnézet tehát alig nagyobb az igazi címkénél; aki
 * „négyszeres nagyítást" vár tőle, rosszul ítéli meg a betűméreteket.
 *
 * Az előnézet doboza a címke ARÁNYÁT követi,
 * ezért a 3:2-es lapon magasabb, mint a korábbi 2:1-esen — ez szándékos: a
 * kliens az előnézet alapján hagyja jóvá a címkét, tehát az előnézetnek az
 * igazi arányt kell mutatnia.
 *
 * `sandbox="allow-scripts"`, és SEMMI MÁS. A dokumentum 2026-09-18 óta egy
 * saját, állandó szkriptet hordoz, ami a nevet a címkére illeszti (lásd
 * `nameFitScript`). Ha az előnézet nem futtatná, a képernyő más betűméretet
 * mutatna, mint a papír — épp az a kettősség, ami miatt az előnézet ezt a
 * dokumentumot tölti be. Az `allow-same-origin` viszont SZÁNDÉKOSAN hiányzik:
 * nélküle a keret egyedi, átlátszatlan eredetben fut, tehát a panel DOM-jához,
 * sütijeihez és tárolójához nem fér hozzá, és nem is navigálhat, nem nyithat
 * ablakot. A cégnév és a név szabad szöveges mező, de escape-elve kerül be; a
 * szkript nem a felhasználótól jön.
 */

import type React from 'react';
import {
  BADGE_HEIGHT_MM,
  BADGE_WIDTH_MM,
  type BadgeContent,
  buildBadgeHtml,
} from '../config/badgePrint';

/** 1 mm CSS-pixelben, 96 dpi-s referencián. A böngésző ugyanezt számolja. */
const PX_PER_MM = 96 / 25.4;

interface BadgePreviewProps {
  content: BadgeContent;
  /** Hány CSS-pixel széles legyen a kinagyított előnézet. */
  width?: number;
}

const BadgePreview: React.FC<BadgePreviewProps> = ({ content, width = 300 }) => {
  const naturalWidth = BADGE_WIDTH_MM * PX_PER_MM;
  const naturalHeight = BADGE_HEIGHT_MM * PX_PER_MM;
  const scale = width / naturalWidth;
  /*
    A keret FELFELÉ kerekítve (#178, 2026-09-21). A 60 × 40 mm 226,77 ×
    151,18 px; a böngésző a keretet egész pixelre veszi, a dokumentum pedig
    pontosan mm-ben áll — a töredékpixel túlcsordult, és a keretben görgetősáv
    jelent meg. A többletet a külső doboz `overflow: hidden`-je vágja le.
  */
  const frameWidth = Math.ceil(naturalWidth);
  const frameHeight = Math.ceil(naturalHeight);

  return (
    <div
      // A külső doboz a KINAGYÍTOTT méretet foglalja, mert a `transform` a
      // tördelésre nem hat: nélküle az előnézet kilógna a modálisból, és a
      // gombokra ülne rá.
      style={{
        width,
        height: naturalHeight * scale,
        overflow: 'hidden',
        flexShrink: 0,
        background: '#fff',
        border: '1px solid rgba(26, 22, 18, 0.2)',
        borderRadius: 6,
        boxShadow: '0 2px 6px rgba(26, 22, 18, 0.08)',
      }}
    >
      <iframe
        title={`${BADGE_WIDTH_MM} x ${BADGE_HEIGHT_MM} mm`}
        aria-label={`${BADGE_WIDTH_MM} x ${BADGE_HEIGHT_MM} mm`}
        sandbox="allow-scripts"
        srcDoc={buildBadgeHtml(content)}
        style={{
          width: frameWidth,
          height: frameHeight,
          border: 0,
          display: 'block',
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      />
    </div>
  );
};

export default BadgePreview;
